import { ChangeEvent, JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridValidRowModel,
} from '@mui/x-data-grid';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNotify, useRefresh } from 'react-admin';
import { read_cookie } from 'sfcookies';
import { hostUrl } from '../utils/url-manager';
import {
    CsvImportConfig,
    IMPORT_MODES,
    ImportAction,
    ImportColumn,
    ImportLookups,
    ImportMode,
    ImportProblems,
    ImportResult,
    ImportRow,
    ImportValues,
    ROW_PROBLEM,
    choicesOf,
} from './csv-import-types';
import {
    actionsFromResult,
    checkRow,
    downloadCsv,
    emptyValues,
    parseCsvFile,
    resolveNamedCells,
    ParsedFile,
    problemsFromResult,
    rowPayload,
    toCsv,
} from './csv-import';
import { useImportLookups } from './useImportLookups';
import { ImportRowDialog } from './ImportRowDialog';
import { ReferenceCell } from './ReferenceCell';
import { AdminMiniDialog } from '../edit/components/NokMini/admin-mini/components/AdminMiniDialog';

type Stage = 'editing' | 'validating' | 'valid' | 'importing';

interface CsvImportDialogProps {
    config: CsvImportConfig;
    open: boolean;
    onClose: () => void;
}

/**
 * Load a CSV, fix it in a grid, validate it against HQ-API, then import every row or none.
 *
 * Import is offered only after a dry run has passed with no edits since, so what is written is
 * exactly what was validated. The mode says what to do with a row that matches a stored record
 * (add new only, add and update, update only); changing it counts as an edit. A passed Validate
 * labels each row New, Update or Unchanged. Problems found in the browser show as the admin edits; HQ-API's
 * own (duplicates, a template of the wrong type) are added by Validate and dropped from a cell
 * once that cell changes.
 */
export const CsvImportDialog = ({ config, open, onClose }: CsvImportDialogProps): JSX.Element => {
    const notify = useNotify();
    const refresh = useRefresh();
    const { lookups, loading: lookupsLoading, failed: lookupsFailed } = useImportLookups(config, open);
    const fileInput = useRef<HTMLInputElement | null>(null);

    const [fileName, setFileName] = useState<string>('');
    const [rows, setRows] = useState<ImportRow[]>([]);
    const [notices, setNotices] = useState<string[]>([]);
    const [serverProblems, setServerProblems] = useState<ImportProblems>({});
    const [stage, setStage] = useState<Stage>('editing');
    const [mode, setMode] = useState<ImportMode>('create');
    const [actions, setActions] = useState<Record<number, ImportAction>>({});
    const [result, setResult] = useState<ImportResult | null>(null);
    const [onlyProblems, setOnlyProblems] = useState<boolean>(false);
    const [editing, setEditing] = useState<ImportRow | null>(null);
    const [confirmingClose, setConfirmingClose] = useState<boolean>(false);
    const keepEditingButton = useRef<HTMLButtonElement | null>(null);
    const validateButton = useRef<HTMLButtonElement | null>(null);
    const askedToClose = useRef<boolean>(false);

    // The question replaces the buttons that had focus, so focus is moved by hand: onto Keep
    // editing when it appears, back onto Validate when it goes. Left alone it falls to the page
    // body, outside the dialog, and Escape and the keyboard stop reaching the dialog.
    useEffect((): void => {
        if (confirmingClose) {
            keepEditingButton.current?.focus();
        } else if (askedToClose.current) {
            validateButton.current?.focus();
        }
        askedToClose.current = confirmingClose;
    }, [confirmingClose]);
    const nextRowId = useRef<number>(1);

    const clientProblems: ImportProblems = useMemo((): ImportProblems => {
        const problems: ImportProblems = {};
        for (const row of rows) {
            const found: Record<string, string> = checkRow(config, row.values, lookups, mode);
            if (Object.keys(found).length > 0) {
                problems[row.rowId] = found;
            }
        }

        return problems;
    }, [config, rows, lookups, mode]);

    // HQ-API's message wins where both have one: it knows why (a template of the wrong type),
    // where the browser only knows the id is not in its list. It is dropped once the cell changes.
    const problemsFor = (rowId: number): Record<string, string> =>
        ({ ...(clientProblems[rowId] ?? {}), ...(serverProblems[rowId] ?? {}) });

    const rowsWithProblems: number = rows.filter((row: ImportRow): boolean =>
        Object.keys(problemsFor(row.rowId)).length > 0).length;

    const positions: Map<number, number> = useMemo((): Map<number, number> =>
        new Map(rows.map((row: ImportRow, index: number): [number, number] => [row.rowId, index + 1])), [rows]);

    /** Any change to the rows means the last Validate no longer describes them. */
    function changed(next: ImportRow[]): void {
        setRows(next);
        setStage('editing');
        setResult(null);
        setActions({});
    }

    /** A new mode asks a different question of every row, so HQ-API's last answers are all stale. */
    function changeMode(next: ImportMode): void {
        setMode(next);
        setServerProblems({});
        changed(rows);
    }

    function reset(): void {
        setFileName('');
        setRows([]);
        setNotices([]);
        setServerProblems({});
        setStage('editing');
        setResult(null);
        setActions({});
        setOnlyProblems(false);
        setEditing(null);
        setConfirmingClose(false);
    }

    /**
     * Closing with rows loaded asks first, in the title bar where Validate and Import were, so the
     * question is in view however far the grid has scrolled. Escape and a click outside ask too.
     */
    function close(): void {
        if (rows.length > 0 && !confirmingClose) {
            setConfirmingClose(true);

            return;
        }
        discard();
    }

    function discard(): void {
        reset();
        onClose();
    }

    async function load(event: ChangeEvent<HTMLInputElement>): Promise<void> {
        const file: File | undefined = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        const parsed: ParsedFile = await parseCsvFile(config, file);
        const found: string[] = [...parsed.parseErrors];
        if (parsed.missingColumns.length > 0) {
            found.push('No column for ' + parsed.missingColumns.join(', ') + '; those cells start empty.');
        }
        if (parsed.ignoredHeaders.length > 0) {
            found.push('Ignored columns: ' + parsed.ignoredHeaders.join(', ') + '.');
        }
        if (parsed.rows.length === 0) {
            found.push('The file has no rows under its header.');
        }
        nextRowId.current = parsed.rows.length + 1;
        reset();
        setFileName(file.name);
        setNotices(found);
        setRows(resolveNamedCells(config, parsed.rows, lookups));
    }

    function addRow(): void {
        const row: ImportRow = { rowId: nextRowId.current++, values: emptyValues(config) };
        changed([...rows, row]);
        setEditing(row);
    }

    function updateRow(rowId: number, values: ImportValues): void {
        const old: ImportRow | undefined = rows.find((row: ImportRow): boolean => row.rowId === rowId);
        if (!old) {
            return;
        }
        const stale: Record<string, string> = { ...(serverProblems[rowId] ?? {}) };
        for (const column of config.columns) {
            if ((old.values[column.key] ?? '') !== (values[column.key] ?? '')) {
                delete stale[column.key];
            }
        }
        setServerProblems({ ...serverProblems, [rowId]: stale });
        changed(rows.map((row: ImportRow): ImportRow => (row.rowId === rowId ? { rowId: rowId, values: values } : row)));
    }

    function setCell(rowId: number, key: string, value: string): void {
        const row: ImportRow | undefined = rows.find((candidate: ImportRow): boolean => candidate.rowId === rowId);
        if (row) {
            updateRow(rowId, { ...row.values, [key]: value });
        }
    }

    function deleteRow(rowId: number): void {
        // HQ-API's messages name rows by position and a duplicate may now be gone, so every one
        // of them is out of date. Validate again to get the current list.
        setServerProblems({});
        changed(rows.filter((row: ImportRow): boolean => row.rowId !== rowId));
    }

    async function send(dryRun: boolean): Promise<void> {
        const sent: ImportRow[] = rows;
        setStage(dryRun ? 'validating' : 'importing');
        let answer: ImportResult;
        try {
            const response: Response = await fetch(hostUrl + config.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'token': read_cookie('auth') as unknown as string,
                },
                body: JSON.stringify({
                    dryRun: dryRun,
                    mode: mode,
                    rows: sent.map((row: ImportRow): ImportValues => rowPayload(config, row.values)),
                }),
            });
            answer = await response.json();
        } catch {
            answer = { status: 'error', message: 'HQ-API could not be reached. Nothing was saved.' };
        }

        setResult(answer);
        const sentRowIds: number[] = sent.map((row: ImportRow): number => row.rowId);
        setServerProblems(problemsFromResult(answer, sentRowIds));
        setActions(actionsFromResult(answer, sentRowIds));
        if (answer.status === 'imported') {
            notify(answer.message, { type: 'success' });
            refresh();
            reset();
            onClose();

            return;
        }
        setStage(answer.status === 'valid' ? 'valid' : 'editing');
        if (answer.status === 'invalid') {
            setOnlyProblems(true);
        }
    }

    function downloadSample(): void {
        downloadCsv(config.fileName + '-sample.csv', toCsv(config, config.sampleRows(lookups)));
    }

    function downloadRows(): void {
        const base: string = fileName.replace(/\.csv$/i, '') || config.fileName;
        downloadCsv(base + '-edited.csv', toCsv(config, rows.map((row: ImportRow): ImportValues => row.values)));
    }

    const columns: GridColDef[] = useMemo((): GridColDef[] => [
        {
            field: '__position',
            headerName: 'Row',
            width: 64,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => positions.get(params.row.rowId),
        },
        {
            field: '__action',
            headerName: 'Action',
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => <ActionChip action={ actions[params.row.rowId] } />,
        },
        {
            field: '__problems',
            headerName: 'Problems',
            width: 280,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <ProblemList problems={ problemsFor(params.row.rowId) } config={ config } />
            ),
        },
        ...config.columns.map((column: ImportColumn): GridColDef => gridColumn(column, lookups, problemsFor, setCell)),
        {
            field: '__actions',
            type: 'actions',
            headerName: '',
            width: 90,
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem key="edit" icon={ <EditOutlinedIcon /> } label="Edit row"
                                     onClick={ () => setEditing(rows.find((row) => row.rowId === params.row.rowId) ?? null) } />,
                <GridActionsCellItem key="delete" icon={ <DeleteOutlineIcon /> } label="Delete row"
                                     onClick={ () => deleteRow(params.row.rowId) } />,
            ],
        },
    // problemsFor reads both problem maps, which are listed instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [config, lookups, positions, rows, serverProblems, clientProblems, actions]);

    const gridRows: GridValidRowModel[] = rows
        .filter((row: ImportRow): boolean => !onlyProblems || Object.keys(problemsFor(row.rowId)).length > 0)
        .map((row: ImportRow): GridValidRowModel => ({ rowId: row.rowId, ...row.values }));

    const busy: boolean = stage === 'validating' || stage === 'importing';
    const canImport: boolean = stage === 'valid' && rows.length > 0 && Object.keys(clientProblems).length === 0;

    return (
        // HQ's sized window, as the admin Mini screens use, so the list stays visible behind it.
        <AdminMiniDialog
            open={ open }
            onClose={ close }
            title={ 'Import ' + config.title }
            subtitle={ fileName || 'From a CSV file' }
            actions={ confirmingClose ?
                <>
                    <Typography role="alert" sx={ { fontSize: '0.875rem', fontWeight: 600, mr: 1 } }>
                        { rows.length === 1 ? 'Discard the loaded row?' : 'Discard the ' + rows.length + ' loaded rows?' }
                    </Typography>
                    <Button ref={ keepEditingButton } variant="outlined" onClick={ () => setConfirmingClose(false) }>
                        Keep editing
                    </Button>
                    <Button variant="contained" color="error" onClick={ discard }>
                        Discard
                    </Button>
                </>
                :
                <>
                    <Button ref={ validateButton } variant="outlined" onClick={ () => send(true) } disabled={ busy || rows.length === 0 }>
                        { stage === 'validating' ? 'Validating...' : 'Validate' }
                    </Button>
                    <Button variant="contained" onClick={ () => send(false) } disabled={ busy || !canImport }>
                        { stage === 'importing' ? 'Importing...' : 'Import ' + rows.length + ' rows' }
                    </Button>
                </>
            }
        >
            { /* The title bar's divider sits right above, and the content starts with no top padding
                 under it, so the gap is made here. */ }
            <Stack spacing={ 2 } sx={ { pt: 2 } }>
                <Stack direction="row" spacing={ 1 } sx={ { flexWrap: 'wrap', alignItems: 'center', rowGap: 1 } }>
                    <input ref={ fileInput } type="file" accept=".csv,text/csv" hidden onChange={ load } />
                    <Button variant="contained" onClick={ () => fileInput.current?.click() } disabled={ busy }>
                        { rows.length > 0 ? 'Choose another file' : 'Choose CSV file' }
                    </Button>
                    <Button variant="outlined" onClick={ downloadSample } disabled={ lookupsLoading }>
                        Download sample CSV
                    </Button>
                    { rows.length > 0 &&
                        <>
                            <Button onClick={ addRow } disabled={ busy }>Add row</Button>
                            <Button onClick={ downloadRows }>Download edited CSV</Button>
                            <FormControlLabel
                                sx={ { ml: 1 } }
                                control={ <Switch checked={ onlyProblems } onChange={ (_e, checked) => setOnlyProblems(checked) } /> }
                                label={ 'Only rows with problems (' + rowsWithProblems + ')' } />
                        </>
                    }
                </Stack>

                <Stack direction="row" spacing={ 2 } sx={ { alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 } }>
                    <FormLabel id="csv-import-mode" sx={ { fontSize: '0.875rem' } }>Rows that match a stored record</FormLabel>
                    <RadioGroup row aria-labelledby="csv-import-mode" value={ mode }
                                onChange={ (_event, value: string) => changeMode(value as ImportMode) }>
                        { IMPORT_MODES.map((option) =>
                            <FormControlLabel key={ option.value } value={ option.value } label={ option.label }
                                              control={ <Radio size="small" disabled={ busy } /> } />) }
                    </RadioGroup>
                </Stack>

                { lookupsFailed &&
                    <Alert severity="warning">
                        The template, signature and industry lists could not be loaded. Ids can still be typed;
                        Validate checks them.
                    </Alert>
                }
                { notices.map((notice: string) => <Alert key={ notice } severity="info">{ notice }</Alert>) }
                { result &&
                    <Alert severity={ result.status === 'valid' ? 'success' : (result.status === 'invalid' ? 'warning' : 'error') }>
                        { result.message }
                    </Alert>
                }

                { rows.length === 0 &&
                    <Typography color="text.secondary">
                        Choose a CSV file to load its rows here, where they can be checked and edited before anything is saved.
                        The sample file shows the columns and the values each one takes. To change stored records,
                        use Export for import on the list, edit that file, and load it here with Add and update or
                        Update only; empty cells keep what is stored.
                    </Typography>
                }

                { rows.length > 0 &&
                    // `width: 0` with `minWidth: 100%` keeps the grid's column widths from widening
                    // the dialog; the grid scrolls sideways inside it instead.
                    <Box sx={ { width: 0, minWidth: '100%' } }>
                        <DataGrid
                            autoHeight
                            rows={ gridRows }
                            columns={ columns }
                            getRowId={ (row: GridValidRowModel) => row.rowId }
                            getRowHeight={ () => 'auto' }
                            disableRowSelectionOnClick
                            initialState={ { pagination: { paginationModel: { pageSize: 25, page: 0 } } } }
                            pageSizeOptions={ [25, 50, 100] }
                            processRowUpdate={ (updated: GridValidRowModel): GridValidRowModel => {
                                const { rowId, ...values } = updated;
                                updateRow(rowId as number, values as ImportValues);

                                return updated;
                            } }
                            sx={ {
                                '& .MuiDataGrid-cell': { py: 0.75, display: 'flex', alignItems: 'center' },
                                '& .import-cell-problem': {
                                    backgroundColor: (theme: Theme) => alpha(theme.palette.error.main, 0.1),
                                    boxShadow: (theme: Theme) => 'inset 0 0 0 1px ' + theme.palette.error.main,
                                },
                            } }
                        />
                    </Box>
                }

            </Stack>

            <ImportRowDialog
                config={ config }
                values={ editing ? editing.values : null }
                title={ editing ? 'Row ' + (positions.get(editing.rowId) ?? '') : '' }
                problems={ editing ? problemsFor(editing.rowId) : {} }
                lookups={ lookups }
                onClose={ () => setEditing(null) }
                onSave={ (values: ImportValues) => {
                    if (editing) {
                        updateRow(editing.rowId, values);
                    }
                    setEditing(null);
                } }
            />
        </AdminMiniDialog>
    );
};

const ACTION_LABELS: Record<ImportAction, { label: string, color: 'success' | 'info' | 'default' }> = {
    create: { label: 'New', color: 'success' },
    update: { label: 'Update', color: 'info' },
    unchanged: { label: 'Unchanged', color: 'default' },
};

/** What a passed Validate says HQ-API will do with the row; nothing until then. */
const ActionChip = ({ action }: { action: ImportAction | undefined }): JSX.Element | null => {
    if (!action) {
        return null;
    }

    return <Chip size="small" variant="outlined" label={ ACTION_LABELS[action].label } color={ ACTION_LABELS[action].color } />;
};

const ProblemList = ({ problems, config }: { problems: Record<string, string>, config: CsvImportConfig }): JSX.Element | null => {
    const entries: [string, string][] = Object.entries(problems);
    if (entries.length === 0) {
        return null;
    }

    return (
        <Box component="ul" sx={ { m: 0, pl: 2, color: 'error.main', fontSize: '0.8125rem', whiteSpace: 'normal' } }>
            { entries.map(([key, message]: [string, string]) => {
                const label: string | undefined = key === ROW_PROBLEM
                    ? undefined
                    : config.columns.find((column: ImportColumn): boolean => column.key === key)?.label;

                return <li key={ key }>{ label ? label + ': ' : '' }{ message }</li>;
            }) }
        </Box>
    );
};

/**
 * One config column as a grid column. Short text and choices edit in the cell; id columns are
 * always-visible dropdowns of the records they can point at; long text is edited in the row dialog.
 */
function gridColumn(
    column: ImportColumn,
    lookups: ImportLookups,
    problemsFor: (rowId: number) => Record<string, string>,
    setCell: (rowId: number, key: string, value: string) => void,
): GridColDef {
    const base: GridColDef = {
        field: column.key,
        headerName: column.label,
        width: column.width ?? 160,
        sortable: false,
        cellClassName: (params): string => (problemsFor(params.row.rowId)[column.key] ? 'import-cell-problem' : ''),
        renderCell: (params: GridRenderCellParams) => {
            const value: string = params.row[column.key] ?? '';
            // A choice shows its label, as its dropdown does; an unrecognised value shows as typed.
            const shown: string = choicesOf(column)?.find((choice): boolean => choice.value === value)?.label ?? value;

            return (
                <Box sx={ { whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflow: 'hidden' } }>
                    { shown }
                </Box>
            );
        },
    };

    if (column.kind === 'reference' || column.kind === 'references') {
        return {
            ...base,
            editable: false,
            renderCell: (params: GridRenderCellParams) => (
                <ReferenceCell
                    value={ params.row[column.key] ?? '' }
                    options={ column.lookup ? lookups[column.lookup] ?? [] : [] }
                    multiple={ column.kind === 'references' }
                    label={ column.label + ', row ' + params.row.rowId }
                    invalid={ !!problemsFor(params.row.rowId)[column.key] }
                    onChange={ (value: string): void => setCell(params.row.rowId, column.key, value) }
                />
            ),
        };
    }

    if (column.kind === 'choice' || column.kind === 'boolean') {
        return {
            ...base,
            editable: true,
            type: 'singleSelect',
            valueOptions: [{ value: '', label: 'None' }, ...(choicesOf(column) ?? [])],
        };
    }

    return { ...base, editable: column.kind === 'text' };
}
